import { useEffect, useMemo, useRef } from 'react';

/** Material UI Packages. */
import { Autocomplete, TextField } from '@mui/material';

/** Internal. */
import { containerSizeList } from 'shared/config/constantsData';
import { CustomSelectCell_string } from 'shared/ui/RenderCell';

import { getHblNumberKey, isAirTab, isOceanExportTab } from './documentUploadUtils';

/* ------------------------------------------------------------------ *
 * Autocomplete edit cells
 * ------------------------------------------------------------------ */

/** Shared popper/listbox sx so MBL autocomplete dropdowns are not clipped inside Dialog/DataGrid. */
const autocompleteListboxSx = {
    maxHeight: 400,
    overflow: 'auto',
};
const autocompleteSx = {
    width: '100%',
    height: '100%',
    '& .MuiInputBase-input': { fontSize: '12px' },
    '& .MuiInputBase-root': { p: 0 },
};
const popperSlotProps = {
    popper: {
        sx: { zIndex: 9999 },
        modifiers: [
            { name: 'flip', enabled: true },
            { name: 'preventOverflow', enabled: true },
        ],
    },
};
const inputSx = { px: 1, '& .MuiInputBase-root': { border: 'none' } };

/** Location display label: name first, then code, then id. */
function getLocationOptionLabel(option) {
    if (option == null) return '';
    if (typeof option === 'string') return option;
    return option.name ?? option.code ?? (option.id != null ? String(option.id) : '');
}

/** DataGrid valueGetter shared by every location column (object value → display string). */
const locationValueGetter = (value) =>
    value && typeof value === 'object'
        ? value.name ?? value.code ?? (value.id != null ? String(value.id) : '')
        : value != null
          ? String(value)
          : '';

/** Focus the edit cell input as soon as the cell enters edit mode. */
function useEditCellFocus(isEditable) {
    const inputRef = useRef(null);
    useEffect(() => {
        if (isEditable && inputRef.current) inputRef.current.focus();
    }, [isEditable]);
    return inputRef;
}

/** Location edit cell: value can be string (from parser) or object { id, code, name }; freeSolo so string displays. */
function LocationAutocompleteCell({ params, options, handleInputChange }) {
    const inputRef = useEditCellFocus(params.isEditable);
    /** Use raw row value so we keep the object; grid may pass valueGetter result (string) as params.value. */
    const value = params.row?.[params.field] ?? params.value;
    return (
        <Autocomplete
            freeSolo
            options={options}
            value={value ?? ''}
            getOptionLabel={getLocationOptionLabel}
            isOptionEqualToValue={(option, v) =>
                option && typeof option === 'object' && v && typeof v === 'object' ? option.id === v.id : option === v
            }
            onChange={(event, newValue) =>
                params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue ?? '' })
            }
            onInputChange={(event, inputValue, reason) => {
                if (reason === 'input') handleInputChange(event, inputValue, params);
            }}
            slotProps={popperSlotProps}
            ListboxProps={{ sx: autocompleteListboxSx }}
            renderInput={(p) => <TextField {...p} inputRef={inputRef} variant="standard" sx={inputSx} />}
            renderOption={(props, option) => (
                <li {...props} key={option?.id ?? option}>
                    {getLocationOptionLabel(option)}
                </li>
            )}
            sx={autocompleteSx}
        />
    );
}

/** Vessel edit cell for the MBL row; same popper/listbox sx as location so dropdown is not clipped. */
function VesselAutocompleteCell({ params, options, handleInputChange }) {
    const inputRef = useEditCellFocus(params.isEditable);
    const value = params.row?.[params.field] ?? params.value;
    const getOptionLabel = (option) => option?.name ?? (typeof option === 'string' ? option : '');
    const handleChange = (event, newValue) => {
        const v = newValue === '' ? '' : typeof newValue === 'string' && newValue.length > 0 ? newValue : newValue?.name;
        params.api.setEditCellValue({ id: params.id, field: params.field, value: v ?? '' });
    };
    return (
        <Autocomplete
            freeSolo
            options={options}
            value={value ?? ''}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, v) => (option?.name ?? '') === (v ?? '')}
            onChange={handleChange}
            onInputChange={(event, inputValue, reason) => {
                if (reason === 'input') handleInputChange(event, inputValue, params);
            }}
            slotProps={popperSlotProps}
            ListboxProps={{ sx: autocompleteListboxSx }}
            renderInput={(p) => <TextField {...p} inputRef={inputRef} variant="standard" sx={inputSx} />}
            renderOption={(props, option) => (
                <li {...props} key={option?.id ?? option?.name}>
                    {getOptionLabel(option)}
                </li>
            )}
            disableClearable
            sx={autocompleteSx}
        />
    );
}

/* ------------------------------------------------------------------ *
 * Column definitions
 * ------------------------------------------------------------------ */

/**
 * MBL and HBL DataGrid column definitions.
 * Memoized so the (heavy) definitions are not rebuilt on every render, as they were when inlined in JSX.
 */
export default function useDocumentGridColumns({
    tabValue,
    vesselOptions,
    locationOptions,
    handleVesselInputChange,
    locationInputHandlers,
}) {
    const mblCols = useMemo(() => {
        const locationCol = (field, headerName) => ({
            field,
            headerName,
            flex: 1,
            minWidth: 90,
            editable: true,
            valueGetter: locationValueGetter,
            renderEditCell: (params) => (
                <LocationAutocompleteCell
                    params={params}
                    options={locationOptions[field]}
                    handleInputChange={locationInputHandlers[field]}
                />
            ),
        });

        if (isAirTab(tabValue)) {
            return [
                { field: 'mawb', headerName: 'MAWB', flex: 1, minWidth: 100, editable: true },
                { field: 'etd', headerName: 'ETD', flex: 1, minWidth: 90, editable: true },
                { field: 'eta', headerName: 'ETA', flex: 1, minWidth: 90, editable: true },
                locationCol('aol', 'AOL'),
                locationCol('aod', 'AOD'),
                locationCol('faod', 'F.AOD'),
            ];
        }
        return [
            { field: 'mbl', headerName: 'MBL', flex: 1, minWidth: 100, editable: true },
            { field: 'etd', headerName: 'ETD', flex: 1, minWidth: 90, editable: true },
            { field: 'eta', headerName: 'ETA', flex: 1, minWidth: 90, editable: true },
            {
                field: 'vessel',
                headerName: 'Vessel',
                flex: 1,
                minWidth: 90,
                editable: true,
                renderEditCell: (params) => (
                    <VesselAutocompleteCell
                        params={params}
                        options={vesselOptions}
                        handleInputChange={handleVesselInputChange}
                    />
                ),
            },
            locationCol('pol', 'POL'),
            locationCol('pod', 'POD'),
            locationCol('fpod', 'F.POD'),
        ];
    }, [tabValue, vesselOptions, locationOptions, handleVesselInputChange, locationInputHandlers]);

    const hblCols = useMemo(() => {
        const hblNumKey = getHblNumberKey(tabValue);
        if (isAirTab(tabValue)) {
            return [
                { field: hblNumKey, headerName: 'HAWB', flex: 1, minWidth: 100, editable: true },
                { field: 'alias', headerName: 'Alias', flex: 1, minWidth: 100, editable: true },
                { field: 'flight', headerName: 'Flight#', flex: 1, minWidth: 90, editable: true },
                { field: 'volume', headerName: 'Volume', flex: 1, minWidth: 80, editable: true },
                { field: 'unit', headerName: 'Unit', flex: 1, minWidth: 70, editable: true },
                { field: 'weight', headerName: 'Weight', flex: 1, minWidth: 80, editable: true },
            ];
        }
        return [
            { field: hblNumKey, headerName: 'HBL', flex: 1, minWidth: 100, editable: true },
            { field: 'containerSummary', headerName: 'Containers', flex: 1, minWidth: 100, editable: true },
            ...(isOceanExportTab(tabValue)
                ? [{ field: 'containerSeal', headerName: 'Seal', flex: 1, minWidth: 90, editable: true }]
                : []),
            {
                field: 'containerVolume',
                headerName: 'Volume',
                flex: 1,
                minWidth: 80,
                editable: true,
                renderEditCell: (params) => <CustomSelectCell_string params={params} options={containerSizeList} />,
            },
        ];
    }, [tabValue]);

    return { mblCols, hblCols };
}
